/* ==========================================
   HEADER SCROLL — StickyHeader Custom Element
   ========================================== */

if (!customElements.get('sticky-header')) {
  class StickyHeader extends HTMLElement {
    constructor() {
      super();
      this.header = this.querySelector('.pompeii-header');
      this.wrapper = this;
      this.threshold = parseInt(this.dataset.scrollThreshold, 10) || 100;
      this.stickyType = this.dataset.stickyType || 'on-scroll-up';
      this.lastScrollY = 0;
      this.ticking = false;
      this.scrolled = false;
      this.hidden = false;
    }

    connectedCallback() {
      if (!CSS.supports('backdrop-filter', 'blur(75px)') &&
          !CSS.supports('-webkit-backdrop-filter', 'blur(75px)')) {
        document.documentElement.classList.add('no-backdrop-filter');
      }

      this.onScroll = this._onScroll.bind(this);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this._update();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
    }

    _onScroll() {
      if (!this.ticking) {
        requestAnimationFrame(() => {
          this._update();
          this.ticking = false;
        });
        this.ticking = true;
      }
    }

    _update() {
      const currentY = window.scrollY;
      const wasScrolled = this.scrolled;

      this.scrolled = currentY > this.threshold;
      if (this.scrolled !== wasScrolled) {
        this.classList.toggle('header-wrapper--scrolled', this.scrolled);
        this.dataset.scrolled = this.scrolled ? 'true' : 'false';
      }

      /* Only hide/show for on-scroll-up; 'always' never hides */
      if (this.stickyType === 'on-scroll-up') {
        if (this.scrolled) {
          const delta = currentY - this.lastScrollY;
          if (delta > 5 && !this.hidden) {
            this.classList.add('header-wrapper--hidden');
            this.hidden = true;
          } else if (delta < -5 && this.hidden) {
            this.classList.remove('header-wrapper--hidden');
            this.hidden = false;
          }
        } else if (this.hidden) {
          this.classList.remove('header-wrapper--hidden');
          this.hidden = false;
        }
      }

      this.lastScrollY = currentY;
    }
  }

  customElements.define('sticky-header', StickyHeader);
}

/* ==========================================
   HEADER GROUP — stack scrolling-bar above header
   ========================================== */
(function setupHeaderStack() {
  function init() {
    const groups = document.querySelectorAll('.shopify-section-group-header-group');
    let bar = null;
    let header = null;
    groups.forEach((g) => {
      if (g.querySelector('.scrolling-bar')) {
        g.classList.add('scrolling-bar-section');
        bar = g;
      } else if (g.querySelector('sticky-header, .header-wrapper')) {
        header = g;
      }
    });
    function measure() {
      const h = bar ? bar.offsetHeight : 0;
      document.documentElement.style.setProperty('--scrolling-bar-height', h + 'px');
      if (header) header.style.top = h + 'px';
    }
    measure();
    if (bar) {
      const ro = new ResizeObserver(measure);
      ro.observe(bar);
    }
    window.addEventListener('resize', measure);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/* ==========================================
   HEADER SEARCH — Toggle overlay
   ========================================== */

if (!customElements.get('header-search')) {
  class HeaderSearch extends HTMLElement {
    connectedCallback() {
      this.overlay = document.getElementById('SearchOverlay');
      if (!this.overlay) return;

      this.input = this.overlay.querySelector('.predictive-search__input, .header__search-input');

      this.querySelectorAll('[data-search-toggle]').forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.open();
        });
      });

      this.overlay.addEventListener('click', (e) => {
        if (e.target === this.overlay) this.close();
      });

      this.overlay.querySelectorAll('[data-search-close]').forEach(btn => {
        btn.addEventListener('click', () => this.close());
      });

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.overlay.classList.contains('is-active')) {
          this.close();
        }
      });
    }

    open() {
      this.overlay.classList.add('is-active');
      document.body.classList.add('search-overlay-open');
      document.body.style.overflow = 'hidden';
      if (this.input) {
        setTimeout(() => this.input.focus(), 200);
      }
    }

    close() {
      this.overlay.classList.remove('is-active');
      document.body.classList.remove('search-overlay-open');
      document.body.style.overflow = '';
    }
  }

  customElements.define('header-search', HeaderSearch);
}

/* ==========================================
   CART COUNTER — Fetch /cart.js
   ========================================== */

if (!customElements.get('cart-counter')) {
  class CartCounter extends HTMLElement {
    connectedCallback() {
      this.countEl = this.querySelector('[data-cart-count]');
      if (!this.countEl) return;
      this.refresh();
      document.addEventListener('cart:updated', () => this.refresh());
    }

    async refresh() {
      try {
        const res = await fetch('/cart.js', { credentials: 'same-origin' });
        if (!res.ok) return;
        const data = await res.json();
        this.countEl.textContent = data.item_count > 0 ? `(${data.item_count})` : '';
      } catch (e) {
        // silently fail
      }
    }
  }

  customElements.define('cart-counter', CartCounter);
}
